if (!customElements.get("variant-picker")) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
    }

    get selectedOptionValues() {
      const options = this.querySelectorAll('.m-product-option select, .m-product-option input');
      const selectedValues = new Map();

      options.forEach((field) => {
        const fieldName = field.name || field.dataset.fieldName;

        if (field.tagName.toLowerCase() === "select") {
          const selectedOption = Array.from(field.options).find(
            (opt) => opt.selected && !opt.disabled
          );
          if (selectedOption) {
            selectedValues.set(fieldName, selectedOption.dataset.optionValueId);
          }
        } else if (
          field.tagName.toLowerCase() === "input" &&
          field.type === "radio"
        ) {
          const checkedInput = field.checked ? field : null;
          if (checkedInput) {
            selectedValues.set(fieldName, checkedInput.dataset.optionValueId);
          }
        }
      });

      options.forEach((field) => {
        const fieldName = field.name || field.dataset.fieldName;

        if (!selectedValues.has(fieldName)) {
          if (field.tagName.toLowerCase() === "select") {
            const firstValidOption = Array.from(field.options).find(
              (opt) => !opt.disabled
            );
            if (firstValidOption) {
              selectedValues.set(
                fieldName,
                firstValidOption.dataset.optionValueId
              );
            }
          } else if (
            field.tagName.toLowerCase() === "input" &&
            field.type === "radio"
          ) {
            const firstInput = field;
            if (firstInput) {
              selectedValues.set(fieldName, firstInput.dataset.optionValueId);
            }
          }
        }
      });

      const result = [];
      options.forEach((field) => {
        const fieldName = field.name || field.dataset.fieldName;
        if (selectedValues.has(fieldName)) {
          const optionValueId = selectedValues.get(fieldName);
          if (optionValueId && !result.includes(optionValueId)) {
            result.push(optionValueId);
          }
        }
      });
      return result;
    }

    // Get product variants — from _themeProducts cache OR from inline JSON in the DOM
    getProductVariants(productId, productInfo) {
      // Primary: use cached product data
      if (window._themeProducts?.[productId]?.variants) {
        return window._themeProducts[productId].variants;
      }

      // Fallback: read from inline <script type="application/json"> in product-info
      const inlineJson = productInfo?.querySelector('script[type="application/json"][data-product-json]')
        || productInfo?.querySelector('script[type="application/json"]');
      if (inlineJson) {
        try {
          const parsed = JSON.parse(inlineJson.textContent);
          const variants = parsed.variants || parsed;
          // Cache it for future use
          if (!window._themeProducts) window._themeProducts = {};
          if (!window._themeProducts[productId]) window._themeProducts[productId] = {};
          window._themeProducts[productId].variants = variants;
          return variants;
        } catch (e) {
          // JSON parse failed, continue to fetch
        }
      }

      // Last resort: fetch product JSON via Ajax and update synchronously via cache
      // (async — will work on next swatch change after first fetch)
      if (!this._fetchingProduct) {
        this._fetchingProduct = true;
        fetch(`/products/${productInfo?.dataset.productHandle}.js`)
          .then(r => r.json())
          .then(data => {
            if (!window._themeProducts) window._themeProducts = {};
            window._themeProducts[productId] = data;
            this._fetchingProduct = false;
          })
          .catch(() => { this._fetchingProduct = false; });
      }

      return null;
    }

    connectedCallback() {
      // Pre-fetch product data into _themeProducts as soon as variant-picker connects
      // so it's ready before the user even clicks a swatch
      const productInfo = this.closest('product-info');
      const productId = productInfo?.dataset.productId;
      const productHandle = productInfo?.dataset.productHandle;

      if (productId && productHandle && !window._themeProducts?.[productId]) {
        fetch(`/products/${productHandle}.js`)
          .then(r => r.json())
          .then(data => {
            if (!window._themeProducts) window._themeProducts = {};
            window._themeProducts[productId] = data;
          })
          .catch(() => {});
      }

      this.addEventListener('change', (event) => {

        // ORIGINAL - keeps price/inventory/buy button working
        const target = this.getInputForEventTarget(event.target);
        MinimogEvents.emit(MinimogTheme.pubSubEvents.optionValueSelectionChange, {
          data: { selectedOptionValues: this.selectedOptionValues, event, target }
        });

        // UPDATE LABEL - instantly update "Color: KARAOKE NIGHT" text
        this.updateSelectedLabel(event.target);

        // NEW - instantly switch image + update cart variant
        const currentProductInfo = this.closest('product-info');
        const currentProductId = currentProductInfo?.dataset.productId;
        const variants = this.getProductVariants(currentProductId, currentProductInfo);

        if (!variants) return; // will retry on next swatch click after fetch completes

        const allFields = Array.from(
          this.querySelectorAll('.m-product-option select, .m-product-option input[type="radio"]:checked')
        );
        const selectedOptions = allFields.map(f => f.value);

        const matchedVariant = variants.find(v =>
          v.options.every((opt, i) => opt === selectedOptions[i])
        );

        if (matchedVariant) {
          // UPDATE CART - update hidden form input so correct variant goes to cart
          currentProductInfo?.querySelectorAll('input[name="id"]').forEach(input => {
            input.value = matchedVariant.id;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });

          // UPDATE URL - only update URL if NOT in quick view modal
          const isQuickView = !!currentProductInfo?.closest('.m-product-quickview, quick-add-modal, [data-view-mode="quick-view"]');
          if (!isQuickView) {
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.set('variant', matchedVariant.id);
            window.history.replaceState({}, '', newUrl.toString());
          }

          // SWITCH IMAGE - only if variant has a featured media
          if (matchedVariant.featured_media) {
            const mediaGallery = currentProductInfo?.querySelector('media-gallery');
            mediaGallery?.setActiveMedia(matchedVariant);
          }
        }
      });
    }

    updateSelectedLabel(changedField) {
      const optionWrapper = changedField.closest('.m-product-option');
      if (!optionWrapper) return;

      const selectedValueEl = optionWrapper.querySelector('span.option-label--selected[data-selected-value]');
      if (!selectedValueEl) return;

      let newValue = '';

      if (changedField.tagName.toLowerCase() === 'select') {
        newValue = changedField.options[changedField.selectedIndex]?.text || '';
      } else if (
        changedField.tagName.toLowerCase() === 'input' &&
        changedField.type === 'radio'
      ) {
        newValue = changedField.dataset.value || changedField.value || '';
      }

      if (newValue) {
        selectedValueEl.textContent = newValue;
        if (window.updateLoveTCShadeCallouts) {
          window.updateLoveTCShadeCallouts(newValue);
        }
      }
    }

    getInputForEventTarget(target) {
      return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
    }
  }
  customElements.define("variant-picker", VariantPicker);
}